import React from 'react';
import wetFloorSignImage from '../../resources/images/falling_down_wet_floor_sign.png'
import parachuteImage from '../../resources/images/parachute.png'
import disconectedImage from '../../resources/images/disconected.png'

type ErrorStateProps = {
    code?: number
    message?: string
    onRetry?: () => void
}

export const ErrorState = ({ code = 500, message, onRetry }: ErrorStateProps) => {
    const errorConfig = {
        500: {
            image: wetFloorSignImage,
            codeText: '500',
            title: 'Internal Server Error',
            desc: message || 'Sorry, something went wrong on our end.',
        },
        403: {
            image: parachuteImage,
            codeText: '403',
            title: 'Forbidden Access',
            desc: message || "You don't have permission to view this page.",
        },
        0: {
            image: disconectedImage,
            codeText: '000',
            title: 'Network Error',
            desc: message || 'Please check your internet connection.',
        },
    }

    const getErrorDetails = (code: number, message?: string) => {
        const base = errorConfig[code];
        if (base) return base;

        // Fallback titles and messages for common but undefined codes
        const fallbackMap: Record<number, { title: string; defaultMessage?: string }> = {
            400: { title: 'Bad Request' },
            401: { title: 'Unauthorized Access' },
            404: { title: 'Something Is Missing', defaultMessage: 'Resource or item could not be found' },
            422: { title: 'Validation Error', defaultMessage: 'Resource or item could not be processed' },
        };

        const fallback = fallbackMap[code] || { title: 'Unexpected Error' };

        return {
            image: parachuteImage,
            codeText: String(code),
            title: fallback.title,
            desc: message || fallback.defaultMessage || 'An unexpected error occurred. Please try again later.',
        };
    };

    const error = getErrorDetails(code, message);

    return (
        <div className="md:my-6 my-4 py-3 px-4 w-full flex align-middle items-center justify-center text-sm">
            <div className="flex flex-col items-center w-full justify-center align-middle">
                <div className="w-full relative">
                    <div className="mx-auto flex-shrink-0 flex items-center justify-center w-44 md:w-56 lg:w-64 relative">
                        <img
                            src={error.image}
                            alt="error_image"
                            className="block text-center m-auto z-10"
                        />
                    </div>

                    <h1 className="text-9xl md:text-[160px] lg:text-[200px] flex items-center justify-center absolute inset-0 font-bold text-red-200/70 dark:text-red-700/40">
                        {error.codeText}
                    </h1>
                </div>

                <div className="text-center flex flex-col items-center justify-center m-auto text-slate-700 dark:text-slate-200 lg:w-full w-full text-sm relative pt-6 md:pt-6 lg:pt-8 pb-4">
                    <div className="w-full flex flex-col align-middle items-center justify-center gap-3">
                        <span className="text-base block text-red-600 dark:text-red-400 silkscreen-regular">
                            {error.title}
                        </span>

                        <span className="text-sm block text-slate-600 dark:text-slate-300">
                            {error.desc}
                        </span>

                        {onRetry && (
                            <button
                                onClick={onRetry}
                                className="mt-4 px-4 py-1.5 text-sm rounded bg-orange-500 text-white hover:bg-orange-600"
                            >
                                Retry
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}